import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth';
import api from '../services/api';
import {
  Check, X, Crown, Zap, Building2, ArrowLeft, Star, TrendingUp,
  Brain, Bell, Database, Shield, BarChart3, Infinity, MessageSquare, Loader
} from 'lucide-react';
import './PricingPage.css';

const { Payments } = api;

const plans = [
  {
    id: 'free', name: 'Free', icon: Zap, price: { monthly: 0, annual: 0 },
    tagline: 'Get started with the basics', popular: false,
    features: [
      { text: '5 AI analyses per day', included: true }, { text: 'Basic charting', included: true },
      { text: 'Top 25 stocks & crypto', included: true }, { text: 'Delayed market data (15 min)', included: true },
      { text: 'Congress trades overview', included: true }, { text: '3 watchlist slots', included: true },
      { text: 'Real-time data', included: false }, { text: 'Advanced screeners', included: false },
      { text: 'AI trade alerts', included: false }, { text: 'AI chat assistant', included: false },
      { text: 'Institutional flow data', included: false }, { text: 'API access', included: false },
    ],
    cta: 'Current Plan', ctaDisabled: true,
  },
  {
    id: 'plus', name: 'Plus', icon: TrendingUp, price: { monthly: 29, annual: 24 },
    tagline: 'Full data for self-directed traders', popular: false,
    features: [
      { text: '10 AI analyses per day', included: true }, { text: 'Advanced TradingView charts', included: true },
      { text: 'All stocks, crypto & ETFs', included: true }, { text: 'Real-time market data', included: true },
      { text: 'Full Congress & insider data', included: true }, { text: '10 watchlist slots', included: true },
      { text: 'Advanced screeners', included: true }, { text: 'AI trade alerts (10/day)', included: true },
      { text: 'AI chat assistant', included: false }, { text: 'Institutional flow data', included: false },
      { text: 'Convergence signals', included: false }, { text: 'API access', included: false },
    ],
    cta: 'Get Plus', ctaDisabled: false,
  },
  {
    id: 'pro', name: 'Pro', icon: Crown, price: { monthly: 49, annual: 41 },
    tagline: 'AI-powered edge for serious traders', popular: true,
    features: [
      { text: 'Everything in Plus', included: true }, { text: 'Unlimited AI analyses', included: true },
      { text: 'Full AI chat assistant', included: true }, { text: 'AI trade alerts (50/day)', included: true },
      { text: 'Unlimited watchlists', included: true }, { text: 'Institutional flow data', included: true },
      { text: 'Convergence signal detection', included: true }, { text: 'Insider trading signals', included: true },
      { text: 'Portfolio insights', included: true }, { text: 'Lobbying & gov contract data', included: true },
      { text: 'Priority data refresh', included: true }, { text: 'API access', included: false },
      { text: 'Custom data exports', included: false },
    ],
    cta: 'Upgrade to Pro', ctaDisabled: false,
  },
  {
    id: 'enterprise', name: 'Enterprise', icon: Building2, price: { monthly: 149, annual: 124 },
    tagline: 'Institutional-grade intelligence', popular: false,
    features: [
      { text: 'Everything in Pro', included: true }, { text: 'Unlimited AI trade alerts', included: true },
      { text: 'Full API access', included: true }, { text: 'Custom data exports (CSV/JSON)', included: true },
      { text: 'Political donation tracking', included: true }, { text: 'Senate disclosure analysis', included: true },
      { text: 'Portfolio backtesting', included: true }, { text: 'White-glove onboarding', included: true },
      { text: 'Priority support', included: true }, { text: 'Early access to new features', included: true },
      { text: 'Custom integrations', included: true },
    ],
    cta: 'Go Enterprise', ctaDisabled: false,
  },
];

const faqs = [
  { q: 'Can I cancel anytime?', a: 'Yes. Cancel your subscription at any time from your Settings page. You\'ll retain access until the end of your billing period.' },
  { q: 'What payment methods do you accept?', a: 'We accept all major credit cards, debit cards, and Apple Pay through our secure payment processor, Stripe.' },
  { q: 'What\'s the difference between Plus and Pro?', a: 'Plus gives you full data access with limited AI — perfect for traders who do their own research. Pro adds the full AI chat assistant, unlimited analyses, convergence signals, and institutional flow data for traders who want an AI-powered edge.' },
  { q: 'Is there a free trial?', a: 'We offer a 7-day free trial on Plus and Pro so you can experience the platform before committing.' },
  { q: 'What happens to my data if I downgrade?', a: 'Your watchlists, alerts, and analysis history are preserved. You\'ll lose access to premium features but can re-upgrade anytime to restore them.' },
  { q: 'Do you offer refunds?', a: 'We offer a full refund within 14 days of your first payment if you\'re not satisfied. No questions asked.' },
  { q: 'Is my financial data secure?', a: 'ELEMENTAL uses TLS 1.3 encryption, JWT authentication, and never stores brokerage credentials.' },
];

function PricingPage() {
  const navigate = useNavigate();
  const { user, isPro, isEnterprise } = useAuth();
  const [billing, setBilling] = useState('monthly');
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [error, setError] = useState('');

  const currentPlan = isEnterprise ? 'enterprise' : isPro ? 'pro' : user?.subscription || 'free';

  const handleSelectPlan = async (planId) => {
    if (planId === currentPlan || planId === 'free') return;
    if (!user) { navigate('/signup'); return; }

    setLoadingPlan(planId);
    setError('');

    try {
      await Payments.createCheckout(planId);
    } catch (err) {
      console.error('Checkout error:', err);
      setError(err.message || 'Failed to start checkout. Please try again.');
      setLoadingPlan(null);
    }
  };

  const getCtaText = (plan) => {
    if (loadingPlan === plan.id) return 'Redirecting...';
    if (plan.id === currentPlan) return 'Current Plan';
    if (plan.id === 'free' && currentPlan !== 'free') return 'Downgrade';
    return plan.cta;
  };

  const isCtaDisabled = (plan) => plan.id === currentPlan || loadingPlan !== null;

  return (
    <div className="pricing-page">
      <div className="pricing-header">
        <button className="pricing-back-btn" onClick={() => navigate(-1)}>
          <ArrowLeft size={18} /><span>Back</span>
        </button>
        <div className="pricing-header-content">
          <div className="pricing-badge"><Star size={14} /><span>Pricing</span></div>
          <h1>Choose Your Edge</h1>
          <p>Trade smarter with political intelligence, AI signals, and institutional-grade data.</p>
        </div>
        <div className="billing-toggle">
          <button className={`billing-option ${billing === 'monthly' ? 'active' : ''}`} onClick={() => setBilling('monthly')}>Monthly</button>
          <button className={`billing-option ${billing === 'annual' ? 'active' : ''}`} onClick={() => setBilling('annual')}>Annual<span className="save-badge">Save 17%</span></button>
        </div>
      </div>

      {error && (
        <div style={{ maxWidth: 800, margin: '0 auto 20px', padding: '12px 16px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 10, color: '#f87171', display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 14 }}>
          <span>{error}</span>
          <button onClick={() => setError('')} style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', fontSize: 18 }}>×</button>
        </div>
      )}

      <div className="pricing-grid four-tier">
        {plans.map((plan) => (
          <div key={plan.id} className={`pricing-card ${plan.popular ? 'popular' : ''} ${plan.id === currentPlan ? 'current' : ''}`}>
            {plan.popular && (<div className="popular-badge"><Crown size={12} />Most Popular</div>)}
            {plan.id === currentPlan && (<div className="current-badge">Your Plan</div>)}
            <div className="pricing-card-header">
              <div className="plan-icon-wrapper"><plan.icon size={24} /></div>
              <h3 className="plan-name">{plan.name}</h3>
              <p className="plan-tagline">{plan.tagline}</p>
            </div>
            <div className="pricing-card-price">
              <span className="price-currency">$</span>
              <span className="price-amount">{billing === 'monthly' ? plan.price.monthly : plan.price.annual}</span>
              {plan.price.monthly > 0 && <span className="price-period">/mo</span>}
              {plan.price.monthly === 0 && <span className="price-period">forever</span>}
            </div>
            {billing === 'annual' && plan.price.monthly > 0 && (
              <div className="annual-note">Billed ${plan.price.annual * 12}/year<span className="annual-savings"> (save ${(plan.price.monthly - plan.price.annual) * 12}/yr)</span></div>
            )}
            <button className={`pricing-cta ${plan.popular ? 'primary' : ''} ${isCtaDisabled(plan) ? 'disabled' : ''}`} onClick={() => handleSelectPlan(plan.id)} disabled={isCtaDisabled(plan)}>
              {loadingPlan === plan.id && <Loader size={16} className="spin" />}
              {getCtaText(plan)}
            </button>
            <div className="pricing-features">
              {plan.features.map((feature, idx) => (
                <div key={idx} className={`pricing-feature ${feature.included ? 'included' : 'excluded'}`}>
                  {feature.included ? <Check size={16} className="feature-icon included" /> : <X size={16} className="feature-icon excluded" />}
                  <span>{feature.text}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="comparison-section">
        <h2>Compare All Features</h2>
        <div className="comparison-table-wrapper">
          <table className="comparison-table">
            <thead><tr><th>Feature</th><th>Free</th><th>Plus</th><th className="highlight">Pro</th><th>Enterprise</th></tr></thead>
            <tbody>
              <tr><td><Brain size={14} /> AI Analyses</td><td>5/day</td><td>10/day</td><td className="highlight">Unlimited</td><td>Unlimited</td></tr>
              <tr><td><MessageSquare size={14} /> AI Chat</td><td><X size={14} className="table-x" /></td><td><X size={14} className="table-x" /></td><td className="highlight"><Check size={14} className="table-check" /></td><td><Check size={14} className="table-check" /></td></tr>
              <tr><td><Bell size={14} /> AI Trade Alerts</td><td><X size={14} className="table-x" /></td><td>10/day</td><td className="highlight">50/day</td><td>Unlimited</td></tr>
              <tr><td><BarChart3 size={14} /> Market Data</td><td>15 min delay</td><td>Real-time</td><td className="highlight">Real-time</td><td>Real-time</td></tr>
              <tr><td><TrendingUp size={14} /> Stocks & Crypto</td><td>Top 25</td><td>All</td><td className="highlight">All</td><td>All</td></tr>
              <tr><td><Database size={14} /> Congress Trades</td><td>Overview</td><td>Full Data</td><td className="highlight">Full + Signals</td><td>Full + Signals</td></tr>
              <tr><td><Database size={14} /> Insider Trading</td><td><X size={14} className="table-x" /></td><td><Check size={14} className="table-check" /></td><td className="highlight"><Check size={14} className="table-check" /></td><td><Check size={14} className="table-check" /></td></tr>
              <tr><td><Database size={14} /> Institutional Flow</td><td><X size={14} className="table-x" /></td><td><X size={14} className="table-x" /></td><td className="highlight"><Check size={14} className="table-check" /></td><td><Check size={14} className="table-check" /></td></tr>
              <tr><td><Database size={14} /> Lobbying & Gov</td><td><X size={14} className="table-x" /></td><td><X size={14} className="table-x" /></td><td className="highlight"><Check size={14} className="table-check" /></td><td><Check size={14} className="table-check" /></td></tr>
              <tr><td><Shield size={14} /> API Access</td><td><X size={14} className="table-x" /></td><td><X size={14} className="table-x" /></td><td><X size={14} className="table-x" /></td><td><Check size={14} className="table-check" /></td></tr>
              <tr><td><Infinity size={14} /> Custom Exports</td><td><X size={14} className="table-x" /></td><td><X size={14} className="table-x" /></td><td><X size={14} className="table-x" /></td><td>CSV / JSON</td></tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="pricing-faq">
        <h2>Frequently Asked Questions</h2>
        <div className="faq-list">
          {faqs.map((faq, idx) => (
            <div key={idx} className={`faq-item ${expandedFaq === idx ? 'expanded' : ''}`} onClick={() => setExpandedFaq(expandedFaq === idx ? null : idx)}>
              <div className="faq-question"><span>{faq.q}</span><span className="faq-toggle">{expandedFaq === idx ? '−' : '+'}</span></div>
              {expandedFaq === idx && <div className="faq-answer">{faq.a}</div>}
            </div>
          ))}
        </div>
      </div>

      <div className="pricing-bottom-cta">
        <h2>Ready to trade with an edge?</h2>
        <p>Join thousands of traders using political intelligence to make smarter decisions.</p>
        <button className="bottom-cta-btn" onClick={() => handleSelectPlan('pro')}>
          {loadingPlan === 'pro' ? <><Loader size={16} className="spin" /> Redirecting...</> : 'Start 7-Day Free Trial'}
        </button>
        <span className="bottom-cta-note">No credit card required to start</span>
      </div>
    </div>
  );
}

export default PricingPage;